/**
 * CardListView — lista em cards do PainelTabela abaixo de 1024px.
 *
 * Redesenho mobile do legado (2026-09-24, opção escolhida pelo dono):
 * card compacto de 3 linhas, tudo alinhado à esquerda.
 *
 *   ┌──────────────────────────────────┐
 *   │ ALEXANDRE JOSÉ DA SILVA  ● Vencido│  ← coluna principal + status
 *   │ Extra · Suíte Lívia e Samara      │  ← coluna secundária
 *   │ entrega 10/11/26 · fila 236 · …   │  ← demais colunas, inline
 *   └──────────────────────────────────┘
 *
 * Principal = coluna `cliente` (ou a primeira); secundária = `ambiente`
 * (ou a segunda, se não for status); status vira selo à direita do título;
 * o resto vira "rótulo valor" separado por ponto, na ordem das colunas.
 * `column.labelCurto` encurta o rótulo nessa linha ("entrega" em vez de
 * "Entrega Prevista"). Colunas com `render` custom continuam funcionando.
 *
 * Sem animação por card de propósito: as telas do fluxo renderizam 400+
 * linhas sem paginação e o stagger do framer-motion travava a thread.
 */

import React from "react";
import { formatarData } from "../../utils/calculos";
import {
  CardList,
  CardItem,
  CardTopo,
  CardTitulo,
  CardSub,
  CardMeta,
  CardVazio,
} from "./styles";

const CHAVE_PRINCIPAL = "cliente";
const CHAVE_SECUNDARIA = "ambiente";
const CHAVE_STATUS = "status";

function separarColunas(colunas) {
  const status = colunas.find((c) => c.key === CHAVE_STATUS) || null;
  const semStatus = colunas.filter((c) => c !== status);
  const principal =
    semStatus.find((c) => c.key === CHAVE_PRINCIPAL) || semStatus[0] || null;
  const restante = semStatus.filter((c) => c !== principal);
  const secundaria =
    restante.find((c) => c.key === CHAVE_SECUNDARIA) || restante[0] || null;
  const meta = restante.filter((c) => c !== secundaria);
  return { principal, secundaria, status, meta };
}

function rotuloCurto(column) {
  if (column.labelCurto) return column.labelCurto;
  return String(column.label || "").toLowerCase();
}

// Na linha de metadados a data sai com ano de 2 dígitos ("10/11/26"): são
// 3 ou 4 datas por card e o ano cheio empurrava a linha para duas.
function valorMeta(row, column, renderCellValue) {
  if (!column.render && column.tipo === "data") {
    const texto = formatarData(row[column.key]);
    return typeof texto === "string" ? texto.replace(/\/(\d{2})(\d{2})$/, "/$2") : texto;
  }
  return renderCellValue(row, column, "cozy");
}

export default function CardListView({
  colunas,
  linhas,
  renderCellValue,
  onRowClick,
  fluxo = false,
  tomDaLinha,
  precosVisiveis = true,
}) {
  if (!linhas || linhas.length === 0) {
    return (
      <CardList>
        <CardVazio>Nenhum registro encontrado</CardVazio>
      </CardList>
    );
  }

  const { principal, secundaria, status, meta: metaTodas } = separarColunas(colunas);
  // Preço oculto por permissão vira "••••••" na tabela; no card isso é só
  // ruído numa linha que já é apertada — some.
  const meta = precosVisiveis ? metaTodas : metaTodas.filter((c) => c.tipo !== "moeda");
  const clicavel = typeof onRowClick === "function";

  return (
    <CardList>
      {linhas.map((row, idx) => {
        const tom = fluxo && tomDaLinha ? tomDaLinha(row) : "default";
        const abrir = clicavel ? () => onRowClick(row) : undefined;
        return (
          <CardItem
            key={`card-${row.cliente ?? row.id ?? row.ambiente ?? "linha"}-${idx}`}
            $barra={tom}
            $clicavel={clicavel}
            role={clicavel ? "button" : undefined}
            tabIndex={clicavel ? 0 : undefined}
            onClick={abrir}
            onKeyDown={
              clicavel
                ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onRowClick(row);
                    }
                  }
                : undefined
            }
          >
            <CardTopo>
              {principal && (
                <CardTitulo title={String(row[principal.key] ?? "")}>
                  {renderCellValue(row, principal, "cozy")}
                </CardTitulo>
              )}
              {status && <span className="selo">{renderCellValue(row, status, "cozy")}</span>}
            </CardTopo>
            {secundaria && (
              <CardSub title={String(row[secundaria.key] ?? "")}>
                {renderCellValue(row, secundaria, "cozy")}
              </CardSub>
            )}
            {meta.length > 0 && (
              <CardMeta>
                {meta.map((column, i) => (
                  // O separador vive DENTRO do par: quando a linha quebra, o
                  // ponto nunca fica pendurado no fim da linha anterior.
                  <span className="par" key={column.key}>
                    {i > 0 && <span className="sep" aria-hidden="true">· </span>}
                    <span className="k">{rotuloCurto(column)}</span>{" "}
                    <b>{valorMeta(row, column, renderCellValue)}</b>
                  </span>
                ))}
              </CardMeta>
            )}
          </CardItem>
        );
      })}
    </CardList>
  );
}
